import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import SafeIcon from '../../common/SafeIcon';
import Logo from '../../assets/logo';
import * as FiIcons from 'react-icons/fi';

const { FiMenu, FiX, FiUser, FiLogOut, FiHome, FiSettings, FiFileText, FiUsers, FiBarChart2, FiPackage, FiImage, FiEdit, FiList, FiShield, FiPhone } = FiIcons;

// Mobile detection utility
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);

  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768); // md breakpoint
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return isMobile;
};

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { user, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [logoError, setLogoError] = useState(false);
  const isMobile = useIsMobile();
  const location = useLocation();
  const onServiceDetails = location.pathname.startsWith('/service-details/');
  const currentServiceId = onServiceDetails ? location.pathname.split('/').pop() : null;

  const handleLogout = () => {
    logout();
    navigate('/');
    setIsOpen(false);
  };

  const getDashboardLink = () => {
    if (!user) return '/';
    // On mobile, redirect admin and operator to home page
    if (isMobile && (user.role === 'admin' || user.role === 'operator')) {
      return '/';
    }
    switch (user.role) {
      case 'customer': return '/customer';
      case 'operator': return '/operator';
      case 'admin': return '/admin';
      default: return '/';
    }
  };



  return (
    <nav className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 relative">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            {logoError ? (
              <Logo width="40" height="40" />
            ) : (
              <img src="/home image.jpeg" alt="How2Apply logo" className="h-10 w-10 object-cover rounded-md" onError={() => setLogoError(true)} />
            )}
            <span className="text-xl font-bold text-gray-900">How2Apply</span>
          </Link>

          {/* Centered phone number on md+ only */}
          <div className="hidden md:flex absolute inset-0 items-center justify-center pointer-events-none">
            <a href="tel:8367013267" className="pointer-events-auto flex items-center gap-2 text-primary-700 hover:text-primary-800 font-semibold">
              <SafeIcon icon={FiPhone} className="w-5 h-5" />
              <span>8367013267</span>
            </a>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-6">
            {isAuthenticated ? (
              <>
                <Link to={getDashboardLink()} className="text-gray-700 hover:text-primary-600 transition-colors">
                  Dashboard
                </Link>

                {user?.role === 'admin' && onServiceDetails && (
                  <Link to={`/admin/service-editor${currentServiceId ? `?serviceId=${currentServiceId}` : ''}`} className="flex items-center space-x-1 text-gray-700 hover:text-primary-600 transition-colors">
                    <SafeIcon icon={FiEdit} className="w-4 h-4" />
                    <span>Edit Service</span>
                  </Link>
                )}
                {user?.role === 'customer' && (
                  <>
                    <Link to="/membership" className="text-gray-700 hover:text-primary-600 transition-colors">
                      Membership
                    </Link>
                  </>
                )}

                <div className="flex items-center space-x-3">
                  <div className="flex items-center space-x-2">
                    <SafeIcon icon={FiUser} className="w-5 h-5 text-gray-600" />
                    <span className="text-sm text-gray-700">{user.name}</span>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="flex items-center space-x-1 text-gray-700 hover:text-red-600 transition-colors"
                  >
                    <SafeIcon icon={FiLogOut} className="w-4 h-4" />
                    <span>Logout</span>
                  </button>
                </div>
              </>
            ) : (
              <div className="flex items-center space-x-4">
                <Link to="/login" className="text-gray-700 hover:text-primary-600 transition-colors">
                  Login
                </Link>
                <Link to="/register" className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors">
                  Register
                </Link>
                {/* Hide admin login on mobile */}
                {!isMobile && (
                  <Link to="/admin-login" className="flex items-center space-x-1 bg-gray-800 text-white px-3 py-2 rounded-lg hover:bg-gray-700 transition-colors">
                    <SafeIcon icon={FiShield} className="w-4 h-4" />
                    <span>Admin</span>
                  </Link>
                )}

              </div>
            )}
          </div>

          {/* Mobile actions: phone + menu */}
          <div className="md:hidden flex items-center gap-4">
            <a href="tel:8367013267" className="flex items-center gap-1 text-primary-600 hover:text-primary-700 font-semibold">
              <SafeIcon icon={FiPhone} className="w-5 h-5" />
              <span>8367013267</span>
            </a>
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-gray-700 hover:text-primary-600 transition-colors"
            >
              <SafeIcon icon={isOpen ? FiX : FiMenu} className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="md:hidden py-4 border-t border-gray-200"
          >
            {isAuthenticated ? (
              <div className="space-y-3">
                <Link
                  to={getDashboardLink()}
                  className="block text-gray-700 hover:text-primary-600 transition-colors"
                  onClick={() => setIsOpen(false)}
                >
                  Dashboard
                </Link>

                {user?.role === 'admin' && onServiceDetails && (
                  <Link
                    to={`/admin/service-editor${currentServiceId ? `?serviceId=${currentServiceId}` : ''}`}
                    className="block flex items-center space-x-2 text-gray-700 hover:text-primary-600 transition-colors"
                    onClick={() => setIsOpen(false)}
                  >
                    <SafeIcon icon={FiEdit} className="w-4 h-4" />
                    <span>Edit Service</span>
                  </Link>
                )}
                {user?.role === 'customer' && (
                  <>
                    <Link
                      to="/membership"
                      className="block text-gray-700 hover:text-primary-600 transition-colors"
                      onClick={() => setIsOpen(false)}
                    >
                      Membership
                    </Link>
                  </>
                )}

                <div className="pt-3 border-t border-gray-200">
                  <div className="flex items-center space-x-2 mb-3">
                    <SafeIcon icon={FiUser} className="w-5 h-5 text-gray-600" />
                    <span className="text-sm text-gray-700">{user.name}</span>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="flex items-center space-x-2 text-red-600 hover:text-red-700 transition-colors"
                  >
                    <SafeIcon icon={FiLogOut} className="w-4 h-4" />
                    <span>Logout</span>
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <Link
                  to="/login"
                  className="block text-gray-700 hover:text-primary-600 transition-colors"
                  onClick={() => setIsOpen(false)}
                >
                  Login
                </Link>
                <Link
                  to="/register"
                  className="block bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors text-center"
                  onClick={() => setIsOpen(false)}
                >
                  Register
                </Link>
                {/* Hide admin login on mobile in mobile menu too */}
                {!isMobile && (
                  <Link
                    to="/admin-login"
                    className="flex items-center justify-center space-x-2 bg-gray-800 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
                    onClick={() => setIsOpen(false)}
                  >
                    <SafeIcon icon={FiShield} className="w-4 h-4" />
                    <span>Admin Login</span>
                  </Link>
                )}

              </div>
            )}
          </motion.div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;